import { useState, useEffect, useCallback } from 'react';
import { Form, Spinner, Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { BsPencil, BsTrash, BsXCircle, BsClock } from 'react-icons/bs';
import { useSelector } from 'react-redux';
import StarDivider from '../components/uiComponents/StarDivider';
import { broadcastService } from '../api/broadcastService';
import Toast from '../components/Toast';
import PortalModal from '../components/PortalModal';
import Pagination from '../components/Pagination';

const EMPTY_FORM = {
  titre: '',
  description: '',
  url: '',
  speaker_name: '',
  location: '',
  thumbnail_url: '',
  date_scheduled: '',
  publication_date: '',
  statut: 'actif',
  is_live: false,
  is_recording: false,
};

const STATUTS = [
  { value: 'actif', label: 'Actif', bg: '#d5f5e3', color: '#1e8449' },
  { value: 'inactif', label: 'Inactif', bg: '#f0f0f0', color: '#666' },
];

function extractError(err) {
  if (err?.response?.data?.errors?.length)
    return err.response.data.errors.map(e => e.message).join(', ');
  return err?.response?.data?.message || err?.message || 'Une erreur est survenue';
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

/* ─── Status badge ───────────────────────────────────────────────────────── */
function StatutBadge({ statut }) {
  const info = STATUTS.find(s => s.value === statut) || { label: statut, bg: '#f0f0f0', color: '#666' };
  return (
    <span style={{
      padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600,
      backgroundColor: info.bg, color: info.color, whiteSpace: 'nowrap',
    }}>
      {info.label}
    </span>
  );
}

export default function BroadcastsPage() {
  const userRole = useSelector((s) => s.auth.user?.role);
  const isViewer = userRole === 'viewer';
  const [broadcasts, setBroadcasts] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editItem, setEditItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ message: '', type: 'success' });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 0 });
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Filters
  const [filters, setFilters] = useState({
    statut: '',
    sortBy: 'date_scheduled',
    sortOrder: 'desc',
    page: 1,
  });

  // Responsive
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const showToast = (message, type = 'success') => setToast({ message, type });
  const hideToast = () => setToast({ message: '', type: 'success' });

  // Load broadcasts
  const loadBroadcasts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: filters.page,
        limit: 10,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
      };
      // Only add statut if it has a value
      if (filters.statut) {
        params.statut = filters.statut;
      }
      const response = await broadcastService.getBroadcasts(params);
      setBroadcasts(response.data);
      setPagination(response.pagination);
    } catch (error) {
      showToast(extractError(error), 'danger');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadBroadcasts();
  }, [loadBroadcasts]);

  // Format date for input
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  // Handle add new
  const openAdd = () => {
    setForm(EMPTY_FORM);
    setEditItem(null);
    setShowModal(true);
  };

  // Handle edit
  const openEdit = async (broadcast) => {
    try {
      const data = await broadcastService.getBroadcastById(broadcast.id);
      setForm({
        ...data,
        date_scheduled: formatDateForInput(data.date_scheduled),
        publication_date: formatDateForInput(data.publication_date),
      });
      setEditItem(data);
      setShowModal(true);
    } catch (error) {
      showToast(extractError(error), 'danger');
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditItem(null);
    setForm(EMPTY_FORM);
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.titre || !form.url) {
      showToast('Title and URL are required', 'danger');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        date_scheduled: new Date(form.date_scheduled).toISOString(),
        publication_date: new Date(form.publication_date).toISOString(),
      };

      if (editItem && form.id) {
        await broadcastService.updateBroadcast(form.id, payload);
        showToast('Diffusion mise à jour avec succès');
      } else {
        await broadcastService.createBroadcast(payload);
        showToast('Diffusion créée avec succès');
      }

      closeModal();
      loadBroadcasts();
    } catch (error) {
      showToast(extractError(error), 'danger');
    } finally {
      setSaving(false);
    }
  };

  // Handle form change
  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  // Handle filter change
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
      page: 1,
    }));
  };

  // Handle delete
  const handleDelete = async () => {
    try {
      await broadcastService.deleteBroadcast(deleteTarget.id);
      showToast('Broadcast deleted successfully');
      setDeleteTarget(null);
      loadBroadcasts();
    } catch (error) {
      showToast(extractError(error), 'danger');
      setDeleteTarget(null);
    }
  };

  const hasActiveFilters = filters.statut || filters.sortBy !== 'date_scheduled';

  const clearFilters = () => {
    setFilters({ statut: '', sortBy: 'date_scheduled', sortOrder: 'desc', page: 1 });
  };

  return (
    <div>
      <Toast message={toast.message} type={toast.type} onClose={hideToast} />

      {/* Header */}
      <div className="text-center mb-3">
        <h5 style={{ fontFamily: 'serif', letterSpacing: 3, fontWeight: 400 }}>DIFFUSION EN DIRECT</h5>
        <StarDivider lineWidth="5%" />
      </div>

      {/* Filters bar */}
      <div className="bg-white p-3 rounded mb-4" style={{ border: '1px solid #eee' }}>
        <div className="d-flex flex-wrap gap-2 align-items-end">
          <div style={{ flex: '1 1 150px' }}>
            <Form.Label className="small fw-semibold d-block mb-1">Statut</Form.Label>
            <Form.Select
              size="sm"
              name="statut"
              value={filters.statut}
              onChange={handleFilterChange}>
              <option value="">Tous les statuts</option>
              {STATUTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </Form.Select>
          </div>
          <div style={{ flex: '1 1 150px' }}>
            <Form.Label className="small fw-semibold d-block mb-1">Trier par</Form.Label>
            <Form.Select
              size="sm"
              name="sortBy"
              value={filters.sortBy}
              onChange={handleFilterChange}>
              <option value="date_scheduled">Date programmée</option>
              <option value="created_at">Date de création</option>
            </Form.Select>
          </div>

          <div style={{ flex: '1 1 150px' }}>
            <Form.Label className="small fw-semibold d-block mb-1">Ordre</Form.Label>
            <Form.Select
              size="sm"
              name="sortOrder"
              value={filters.sortOrder}
              onChange={handleFilterChange}>
              <option value="desc">Décroissant</option>
              <option value="asc">Croissant</option>
            </Form.Select>
          </div>

          {!isViewer && (
            <button onClick={openAdd} className="btn-primary-mcm">
              + Nouvelle diffusion
            </button>
          )}

          {hasActiveFilters && (
            <button onClick={clearFilters} className="btn-outline-mcm">
              <BsXCircle size={12} className="me-1" />
              EFFACER
            </button>
          )}
        </div>
      </div>

      {/* Result count */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="fw-bold mb-0">Résultats ({pagination.total ?? broadcasts.length})</h6>
      </div>

      {/* Content */}
      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" style={{ color: '#8B0000' }} /></div>
      ) : broadcasts.length === 0 ? (
        <div className="text-center py-5 text-secondary">Aucune diffusion trouvée</div>
      ) : isMobile ? (
        /* Mobile card list */
        <div className="d-flex flex-column gap-3">
          {broadcasts.map((broadcast) => (
            <div key={broadcast.id} className="mobile-card">
              <div className="d-flex justify-content-between align-items-start mb-2">
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {broadcast.titre}
                  </div>
                  <div style={{ fontSize: 12, color: '#777', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {broadcast.speaker_name || '—'}
                  </div>
                </div>
                <StatutBadge statut={broadcast.statut} />
              </div>
              <div className="d-flex align-items-center gap-2 mb-2" style={{ fontSize: 11, color: '#888' }}>
                <BsClock size={11} />
                <span>{formatDate(broadcast.date_scheduled)}</span>
              </div>
              {broadcast.description && (
                <p className="mobile-section-text" style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
                  {broadcast.description.substring(0, 80)}...
                </p>
              )}
              <div className="d-flex gap-2 mt-2">
                {!isViewer && (
                  <>
                    <button className="card-btn-edit" onClick={() => openEdit(broadcast)} style={{ borderRadius: '0 0 0 6px' }}>
                      <BsPencil size={13} /> Modifier
                    </button>
                    <button className="card-btn-delete" onClick={() => setDeleteTarget(broadcast)} style={{ borderRadius: '0 0 6px 0' }}>
                      <BsTrash size={13} /> Supprimer
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* Desktop table */
        <div className="table-mcm-wrap" style={{ overflowX: 'auto' }}>
          <table className="table-mcm">
            <thead>
              <tr>
                {['Titre', 'Orateur', 'Lieu', 'Date programmée', 'Statut', 'En direct', ...(isViewer ? [] : ['Actions'])].map(h => (
                  <th key={h} className="th-mcm">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {broadcasts.map((broadcast) => (
                <tr key={broadcast.id}>
                  <td className="td-mcm" style={{ fontWeight: 600 }}>
                    {(() => {
                      const fullTitle = broadcast?.titre?.charAt(0).toUpperCase() + broadcast.titre?.slice(1);
                      const shouldTruncate = fullTitle?.length > 60;
                      const displayTitle = shouldTruncate ? fullTitle.substring(0, 60) + '...' : fullTitle;
                      
                      return shouldTruncate ? (
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip id={`tooltip-title-${broadcast.id}`}>{fullTitle}</Tooltip>}
                        >
                          <span style={{ cursor: 'pointer' }}>{displayTitle}</span>
                        </OverlayTrigger>
                      ) : (
                        <span>{displayTitle}</span>
                      );
                    })()}
                    {broadcast.description && (
                      <p className="small-text" style={{ fontSize: 12, color: '#777', marginTop: 4 }}>
                        {broadcast.description.substring(0, 50)}...
                      </p>
                    )}
                  </td>
                  <td className="td-mcm text-capitalize">{broadcast.speaker_name || '—'}</td>
                  <td className="td-mcm text-capitalize">{broadcast.location || '—'}</td>
                  <td className="td-mcm" style={{ whiteSpace: 'nowrap', color: '#666' }}>{formatDate(broadcast.date_scheduled)}</td>
                  <td className="td-mcm"><StatutBadge statut={broadcast.statut} /></td>
                  <td className="td-mcm">
                    <span style={{
                      padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                      backgroundColor: broadcast.is_live ? '#ffe5e5' : '#f0f0f0',
                      color: broadcast.is_live ? '#8B0000' : '#666',
                    }}>
                      {broadcast.is_live ? '🔴 EN DIRECT' : 'Hors ligne'}
                    </span>
                  </td>
                  {!isViewer && (
                    <td className="td-mcm">
                      <div className="d-flex gap-2">
                        <button className="icon-btn-mcm" title="Modifier" onClick={() => openEdit(broadcast)}>
                          <BsPencil size={15} color="#555" />
                        </button>
                        <button className="icon-btn-mcm" title="Supprimer" onClick={() => setDeleteTarget(broadcast)}>
                          <BsTrash size={15} color="#8B0000" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {broadcasts.length > 0 && (
        <Pagination
          currentPage={filters.page}
          totalPages={pagination.pages}
          onPageChange={page => setFilters(f => ({ ...f, page }))} />
      )}

      {/* Create/Edit Modal */}
      <PortalModal show={showModal} onClose={closeModal} title={editItem ? 'Modifier la diffusion' : 'Nouvelle diffusion'} size="lg">
        <button onClick={closeModal} className="modal-close-btn">×</button>
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label className="modal-label">Titre *</Form.Label>
            <Form.Control
              type="text"
              name="titre"
              value={form.titre}
              onChange={handleFormChange}
              placeholder="Titre de la diffusion"
              required
              style={{ marginTop: 4 }} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="modal-label">Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="description"
              value={form.description}
              onChange={handleFormChange}
              placeholder="Description de la diffusion"
              style={{ marginTop: 4 }} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="modal-label">URL *</Form.Label>
            <Form.Control
              type="url"
              name="url"
              value={form.url}
              onChange={handleFormChange}
              placeholder="https://www.youtube.com/watch?v=..."
              required
              style={{ marginTop: 4 }} />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="modal-label">Orateur</Form.Label>
            <Form.Control
              type="text"
              name="speaker_name"
              value={form.speaker_name}
              onChange={handleFormChange}
              placeholder="Nom de l'orateur"
              style={{ marginTop: 4 }} />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="modal-label">Lieu</Form.Label>
            <Form.Control
              type="text"
              name="location"
              value={form.location}
              onChange={handleFormChange}
              placeholder="Lieu de la diffusion"
              style={{ marginTop: 4 }} />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="modal-label">URL de la miniature</Form.Label>
            <Form.Control
              type="url"
              name="thumbnail_url"
              value={form.thumbnail_url}
              onChange={handleFormChange}
              placeholder="https://..."
              style={{ marginTop: 4 }} />
          </Form.Group>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="modal-label">Date programmée *</Form.Label>
                <Form.Control
                  type="date"
                  name="date_scheduled"
                  value={form.date_scheduled}
                  onChange={handleFormChange}
                  required
                  style={{ marginTop: 4 }} />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="modal-label">Date de publication *</Form.Label>
                <Form.Control
                  type="date"
                  name="publication_date"
                  value={form.publication_date}
                  onChange={handleFormChange}
                  required
                  style={{ marginTop: 4 }} />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="modal-label">Statut</Form.Label>
                <Form.Select
                  name="statut"
                  value={form.statut}
                  onChange={handleFormChange}
                  style={{ marginTop: 4 }}>
                  {STATUTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mt-2">
                <Form.Check
                  type="checkbox"
                  name="is_live"
                  checked={form.is_live}
                  onChange={handleFormChange}
                  label="Marquer comme en direct"
                  style={{ marginTop: 24 }} />
                <Form.Check
                  type="checkbox"
                  name="is_recording"
                  checked={form.is_recording}
                  onChange={handleFormChange}
                  label="Enregistrement" />
              </Form.Group>
            </Col>
          </Row>

          <div style={{ textAlign: 'center', marginTop: 24 }}>
            <button
              type="button"
              onClick={closeModal}
              className="btn-outline-mcm"
              style={{ marginRight: 12 }}>
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving}
              className="btn-primary-mcm"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
              }}>
              {saving ? <><Spinner size="sm" animation="border" /> Enregistrement...</> : editItem ? 'Mettre à jour' : 'Créer'}
            </button>
          </div>
        </Form>
      </PortalModal>

      {/* Delete Modal */}
      <PortalModal show={!!deleteTarget} onClose={() => setDeleteTarget(null)} size="sm">
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <div style={{
            width: 48, height: 48, borderRadius: '50%', backgroundColor: '#ffe5e5',
            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px',
          }}>
            <BsTrash size={22} color="#8B0000" />
          </div>
          <h6 style={{ fontWeight: 700, marginBottom: 2 }}>Supprimer cette diffusion ?</h6>
          <p style={{ fontSize: 13, color: '#777', margin: 0 }}>Cette action est irréversible.</p>
        </div>

        {deleteTarget && (
          <div style={{
            backgroundColor: '#f7f8fa', borderRadius: 8,
            padding: '14px 16px', marginBottom: 24, fontSize: 13,
          }}>
            <div className="mb-1">
              <span className="fw-semibold">{deleteTarget.titre}</span>
            </div>
            <div style={{ color: '#555', fontSize: 12 }}>
              {deleteTarget.speaker_name && <span>Orateur : {deleteTarget.speaker_name}</span>}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
          <button
            onClick={() => setDeleteTarget(null)}
            className="btn-outline-mcm">
            Annuler
          </button>
          <button
            onClick={handleDelete}
            className="btn-primary-mcm">
            Supprimer
          </button>
        </div>
      </PortalModal>
    </div>
  );
}
